import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import * as Yup from "yup";
import { shallow } from "zustand/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import SqlbaseAuthorizationModal from "./SqlbaseAuthorizationModal";
import { Role, useAuthStore } from "../../stores/auth";
import { useSqlbaseStore } from "../../stores/sqlbase";
import { useToastStore } from "../../stores/toast";

function PasswordField({ value, onChange, onBlur, disabled, label, name, error, placeholder }) {
  const [visible, setVisible] = useState(false);
  const inputClassName = `form-control ${error ? "is-invalid" : ""}`;

  return (
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <div className="input-group">
        <input
          type={visible ? "text" : "password"}
          name={name}
          className={inputClassName}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          autoComplete="off"
          placeholder={placeholder}
        />
        <button
          type="button"
          className="btn btn-red"
          onClick={() => setVisible(!visible)}
        >
          <i className={`fa ${visible ? "fa-eye-slash" : "fa-eye"}`} />
        </button>
      </div>
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
}

PasswordField.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  placeholder: PropTypes.string,
};

function SqlbaseSettings() {
  const { t } = useTranslation();
  const [hasRole] = useAuthStore((state) => [state.hasRole], shallow);
  const addToast = useToastStore((state) => state.addToast);

  const [
    natrav,
    authorization,
    hasExisting,
    fetchConfig,
    updateConfig,
    clearConfig,
    checkConnection,
    fetchNatrav,
    checkTunnelConnection,
    fetchAuthorization,
    acceptAuthorization,
  ] = useSqlbaseStore(
    (s) => [
      s.natrav,
      s.authorization,
      s.hasExisting,
      s.fetchConfig,
      s.updateConfig,
      s.clearConfig,
      s.checkConnection,
      s.fetchNatrav,
      s.checkTunnel,
      s.fetchAuthorization,
      s.acceptAuthorization,
    ],
    shallow,
  );

  const [showToken, setShowToken] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [tunnelStatus, setTunnelStatus] = useState(null);
  const [testingTunnel, setTestingTunnel] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalReadOnly, setAuthModalReadOnly] = useState(false);

  const validationSchema = Yup.object().shape({
    db_name: Yup.string(),
    username: Yup.string(),
    password: Yup.string(),
  });

  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const runTest = async (values) => {
    setConnectionStatus(null);
    setTesting(true);
    try {
      const { data: checkResult, error: checkError } = await checkConnection(values);
      if (checkError || checkResult?.status !== "ok") {
        const key = checkResult?.t || "settings.sqlbase.connection_failed";
        setConnectionStatus({ ok: false, message: t(key) });
      } else {
        setConnectionStatus({ ok: true, message: t("settings.sqlbase.connection_ok") });
      }
    } finally {
      setTesting(false);
    }
  };

  const handleTest = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched({ db_name: true, username: true, password: true });
      return;
    }
    await runTest(formik.values);
  };

  const runSave = async (values) => {
    setSaving(true);
    try {
      const { data, error } = await updateConfig(values);
      if (error) {
        const msg = error?.detail?.[0]?.t || error?.detail;
        addToast("error", msg ? t(msg) : JSON.stringify(error));
        return;
      }
      formik.resetForm({ values: data });
      addToast("success", t("common.saved"));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched({ db_name: true, username: true, password: true });
      return;
    }
    if (!authorization?.accepted || formik.dirty) {
      setAuthModalReadOnly(false);
      setShowAuthModal(true);
      return;
    }
    await runSave(formik.values);
  };

  const handleAuthAccept = async () => {
    setShowAuthModal(false);
    const { error } = await acceptAuthorization({ dbName: formik.values.db_name });
    if (error) {
      addToast("error", t("common.error"));
      return;
    }
    await runSave(formik.values);
  };

  const handleAuthCancel = () => {
    setShowAuthModal(false);
  };

  const handleClear = async () => {
    const { error } = await clearConfig();
    if (error) {
      const msg = error?.detail?.[0]?.t || error?.detail;
      addToast("error", msg ? t(msg) : JSON.stringify(error));
      return;
    }
    formik.resetForm({ values: { db_name: "", username: "", password: "" } });
    setConnectionStatus(null);
    addToast("success", t("common.deleted"));
  };

  const formik = useFormik({
    initialValues: { db_name: "", username: "", password: "" },
    validationSchema,
    onSubmit: () => {},
  });

  const handleFieldChange = (e) => {
    setConnectionStatus(null);
    formik.handleChange(e);
  };

  useEffect(() => {
    fetchConfig().then(({ data }) => {
      if (data) {
        const filled = Object.values(data).some((v) => v !== "");
        if (filled) {
          formik.resetForm({ values: data });
        }
      }
    });
    fetchNatrav();
    fetchAuthorization();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTestTunnel = async () => {
    setTunnelStatus(null);
    setTestingTunnel(true);
    try {
      const { data, error } = await checkTunnelConnection();
      if (error || data?.status !== "ok") {
        setTunnelStatus({ ok: false, message: t("settings.natrav.tunnel_failed") });
      } else {
        setTunnelStatus({ ok: true, message: t("settings.natrav.tunnel_ok") });
      }
    } finally {
      setTestingTunnel(false);
    }
  };

  const handleCopyToken = () => {
    if (natrav?.token) {
      navigator.clipboard.writeText(natrav.token);
    }
  };

  if (!hasRole(Role.ADMINISTRATOR)) {
    return <Navigate to="/" replace />;
  }

  function maskedToken(token) {
    if (!token) return "";
    return "●".repeat(token.length);
  }

  return (
    <>
      <Breadcrumb
        items={[
          { label: t("menu.settings"), to: "/settings" },
          { label: t("settings.sqlbase.title") },
        ]}
      />
      <div className="row g-3">
        <div className="col-lg-6">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">{t("settings.natrav.title")}</h5>
              <div className="mb-3">
                <label className="form-label">{t("settings.natrav.server")}</label>
                <input type="text" className="form-control" value={natrav?.remote_host ?? ""} disabled />
              </div>
              <div className="mb-3">
                <label className="form-label">{t("settings.natrav.service")}</label>
                <input type="text" className="form-control" value={natrav?.service_name ?? ""} disabled />
              </div>
              <div className="mb-3">
                <label className="form-label">{t("settings.natrav.token")}</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={showToken ? (natrav?.token ?? "") : maskedToken(natrav?.token)}
                    disabled
                  />
                  <button
                    type="button"
                    className="btn btn-red"
                    onClick={handleCopyToken}
                    title={t("common.copy")}
                  >
                    <i className="fa fa-copy" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-red"
                    onClick={() => setShowToken(!showToken)}
                  >
                    <i className={`fa ${showToken ? "fa-eye-slash" : "fa-eye"}`} />
                  </button>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col">
                  <label className="form-label">{t("settings.natrav.lokal")}</label>
                  <input type="text" className="form-control" value={natrav?.local_host ?? ""} disabled />
                </div>
                <div className="col-4">
                  <label className="form-label">{t("settings.natrav.port")}</label>
                  <input type="text" className="form-control" value={natrav?.local_port ?? ""} disabled />
                </div>
              </div>
              {tunnelStatus && (
                <div className={`alert ${tunnelStatus.ok ? "alert-success" : "alert-danger"} alert-dismissible`}>
                  {tunnelStatus.message}
                  <button type="button" className="btn-close" onClick={() => setTunnelStatus(null)} />
                </div>
              )}
              <button type="button" className="btn btn-success" onClick={handleTestTunnel} disabled={testingTunnel}>
                {testingTunnel && <i className="fa fa-spinner fa-spin me-1" />}
                {t("settings.natrav.test")}
              </button>
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">{t("settings.sqlbase.title")}</h5>
              <form autoComplete="off">
                <div className="mb-3">
                  <label className="form-label">
                    {t("settings.sqlbase.db_name")}
                  </label>
                  <input
                    type="text"
                    name="db_name"
                    className={`form-control ${formik.touched.db_name && formik.errors.db_name ? "is-invalid" : ""}`}
                    value={formik.values.db_name}
                    onChange={handleFieldChange}
                    onBlur={formik.handleBlur}
                    autoComplete="off"
                  />
                  {formik.touched.db_name && formik.errors.db_name && (
                    <div className="invalid-feedback">{formik.errors.db_name}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    {t("settings.sqlbase.username")}
                  </label>
                  <input
                    type="text"
                    name="username"
                    className={`form-control ${formik.touched.username && formik.errors.username ? "is-invalid" : ""}`}
                    value={formik.values.username}
                    onChange={handleFieldChange}
                    onBlur={formik.handleBlur}
                    autoComplete="off"
                  />
                  {formik.touched.username && formik.errors.username && (
                    <div className="invalid-feedback">{formik.errors.username}</div>
                  )}
                </div>
                <PasswordField
                  label={t("settings.sqlbase.password")}
                  value={formik.values.password}
                  onChange={handleFieldChange}
                  name="password"
                  error={formik.touched.password && formik.errors.password}
                  onBlur={formik.handleBlur}
                />
                {connectionStatus && (
                  <div className={`alert ${connectionStatus.ok ? "alert-success" : "alert-danger"} alert-dismissible`}>
                    {connectionStatus.message}
                    <button type="button" className="btn-close" onClick={() => setConnectionStatus(null)} />
                  </div>
                )}
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={testing || saving}
                    onClick={handleTest}
                  >
                    {testing && <i className="fa fa-spinner fa-spin me-1" />}
                    {t("settings.sqlbase.test")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={testing || saving || !connectionStatus?.ok}
                    onClick={handleSave}
                  >
                    {saving && <i className="fa fa-spinner fa-spin me-1" />}
                    {t("common.save")}
                  </button>
                  {hasExisting && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={testing || saving}
                      onClick={handleClear}
                    >
                      {t("common.delete")}
                    </button>
                  )}
                </div>
              </form>
              {authorization?.accepted && (
                <button
                  type="button"
                  className="btn btn-link text-muted mt-3 p-0"
                  onClick={() => { setAuthModalReadOnly(true); setShowAuthModal(true); }}
                >
                  <i className="fa fa-check-circle text-success me-1" />
                  {t("settings.sqlbase.authorization_accepted_by", {
                    name: authorization.accepted_by_name,
                    date: new Date(authorization.accepted_at).toLocaleDateString(),
                    version: authorization.version,
                    db_name: authorization.db_name,
                  })}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {showAuthModal && (
        <SqlbaseAuthorizationModal
          readOnly={authModalReadOnly}
          onAccept={handleAuthAccept}
          onCancel={handleAuthCancel}
        />
      )}
    </>
  );
}

export default SqlbaseSettings;
